import Config from "./config.js";

/**
 * Fields — what goes into a form's hidden inputs, and writing it there.
 *
 * PRECEDENCE, lowest first; a later layer replaces an earlier one for the same field:
 *
 *   1. built-in defaults   utm_medium = "website", country_code = site country,
 *                          current_url / conversion_url = this page
 *   2. options.fields      the caller's values (UTMs included)
 *   3. URL                 utm_* and irclickid → impact_id — this visit's attribution beats any
 *                          value baked into the page, unless the caller forces that field
 *   4. options.owner       → direct_assignment_rep, always forced
 *
 * Then, per field, at write time: a value already in the input (a HubSpot default, or what the
 * visitor typed) WINS unless the field is forced; a field the form does not define is skipped —
 * the embed submits nothing else, so adding the hidden field in HubSpot is what switches it on.
 */
const Fields = {};

const UTMS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];

/** This visit's attribution from a query string: utm_* and impact_id. Empty params are dropped. */
Fields.utms = function (search = window.location?.search ?? "") {
  const params = new URLSearchParams(search);
  const out = {};

  UTMS.forEach((key) => {
    if (params.get(key)) out[key] = params.get(key);
  });

  if (params.get("irclickid")) out.impact_id = params.get("irclickid");

  return out;
};

/**
 * The values to write, and which of them are forced.
 *
 * @param  {object} options  create()'s options — fields, force, owner, utm, country
 * @param  {object} page     { search, href } — the location, injectable for tests
 * @return {{values: object, force: string[]}}
 */
Fields.resolve = function (options = {}, page = window.location ?? {}) {
  const utm = options.utm !== false;
  const values = {};
  const force = Array.isArray(options.force) ? [...options.force] : [];

  if (utm) {
    values.utm_medium = Config.utmMediumDefault();
    values.current_url = page.href || "";
    values.conversion_url = page.href || "";
  }

  if (options.country !== false) values[Config.countryField] = Config.country();

  Object.assign(values, options.fields || {});

  if (utm) Object.assign(values, Fields.utms(page.search || ""));

  // A field the caller FORCES is the caller's to the end: the URL layer doesn't replace it either (the HubSpot form
  // block's Hidden fields — an `impact_id` set on the block beats a link's irclickid).
  if (Array.isArray(options.force))
    options.force.forEach((name) => {
      if (options.fields && name in options.fields) values[name] = options.fields[name];
    });

  if (options.owner) {
    values[Config.ownerField] = options.owner;
    force.push(Config.ownerField);
  }

  return { values, force: options.force === true ? true : force };
};

/**
 * The element type's own setter (HTMLInputElement / HTMLTextAreaElement / HTMLSelectElement.prototype), bypassing the
 * one React puts on the element itself; a plain assignment where there is none (a test's fake element).
 */
const nativeSet = (input, property, value) => {
  const setter = Object.getOwnPropertyDescriptor(Object.getPrototypeOf(input) ?? {}, property)?.set;

  if (setter) setter.call(input, value);
  else input[property] = value;
};

/**
 * THE way to change a field of a HubSpot form. HubSpot's embedded form is a React app: it keeps its own copy of every
 * field and submits THAT. A plain `input.value = …` / `input.checked = …` changes the page but not HubSpot's copy, so
 * the field is submitted empty (or HubSpot redraws it back on its next render). The element's NATIVE setter, then the
 * events React listens for, updates both. Every write the HubSpot form block makes goes through these two.
 *
 * @param  {HTMLInputElement|HTMLTextAreaElement|HTMLSelectElement|null} input
 * @param  {string} value
 * @return {boolean} whether anything changed
 */
Fields.set = function (input, value) {
  if (!input) return false;

  const next = value === null || value === undefined ? "" : String(value);

  if (input.value === next) return false;

  nativeSet(input, "value", next);
  input.dispatchEvent(new Event("input", { bubbles: true }));
  input.dispatchEvent(new Event("change", { bubbles: true }));

  return true;
};

/**
 * Tick or untick a checkbox / pick a radio, the same way: the native setter, then `click` (React's own signal for
 * these — a plain Event, so the browser doesn't toggle it a second time) and `change`.
 *
 * @param  {HTMLInputElement|null} input
 * @param  {boolean} checked
 * @return {boolean} whether anything changed
 */
Fields.check = function (input, checked) {
  if (!input || input.checked === Boolean(checked)) return false;

  nativeSet(input, "checked", Boolean(checked));
  input.dispatchEvent(new Event("click", { bubbles: true }));
  input.dispatchEvent(new Event("change", { bubbles: true }));

  return true;
};

/**
 * Write values into a rendered form.
 *
 * @param  {HTMLFormElement} form
 * @param  {object}          values  { fieldName: value } — null / undefined / "" are ignored
 * @param  {{force?: string[]|true}} opts  names that overwrite an existing value, or true for all
 * @return {string[]} the names actually written
 */
Fields.fill = function (form, values = {}, { force = [] } = {}) {
  const written = [];

  if (!form?.querySelector) return written;

  Object.entries(values).forEach(([name, value]) => {
    if (value === null || value === undefined || value === "") return;

    const input = form.querySelector(`[name="${name}"]`);

    if (!input) return;

    const forced = force === true || (Array.isArray(force) && force.includes(name));

    // A VISIBLE field is the visitor's to fill — never pre-fill it unless the caller forces it (a
    // referral form's visible city/state would describe the referred business, not the advisor).
    if (!forced && input.type !== "hidden") return;

    if (input.value && !forced) return;

    Fields.set(input, value);
    written.push(name);
  });

  return written;
};

export default Fields;
